import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import {
  Badge,
  ChakraBox,
  ChakraFlex,
  ChakraGridItem,
  ChakraSimpleGrid,
  ChakraTab,
  ChakraTabList,
  ChakraTabs,
  ChakraText,
  EquipUITheme,
} from '@equip.health/ui';
import { useHistory } from 'react-router-dom';

import NoData from '~/components/common/NoData';
import { ApiCommand } from '~/lib/Api';
import { Listeners } from '~/lib/constants/global.constants';
import { PatientTabs } from '~/lib/constants/patients.constants';
import { PatientTabsCollection } from '~/lib/hooks/usePatientTabsCollection';
import { OrganizeBy } from '~/lib/constants/task.constants';
import urlConstants from '~/lib/constants/url.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useTaskContext } from '~/lib/contexts/TaskContext';
import { useApi } from '~/lib/hooks';
import {
  CompServTask,
  FetchTasksResponse,
  getEmptyTaskContent,
  isPatientTaskDetailRoute,
  mapCompServTaskToTask,
} from '~/lib/util/task.util';
import Loading from '~/pages/loading';
import { Path } from '~/routes/constants';

import EmptyTaskList from '../../../tasks/EmptyTaskList';
import TaskDetail from '../../../tasks/TaskDetail';
import TaskList from '../../../tasks/TaskList/TaskList';
import TaskListItem from '../../../tasks/TaskList/TaskListItem';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { TaskEvents } from '~/analytics/taskEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';

/** 100% of the view after allowing for the patient header (~200px) + task filter (~60px) and total count footer (~50px) */
const TASKS_CONTAINER_HEIGHT = 'calc(100vh - 310px)';

/** Number of tasks to fetch per page on the tasks tab */
const ALL_TASKS_PAGE_SIZE = 50;

/** Number of tasks to show on the overview tab */
const TASKS_PREVIEW_PAGE_SIZE = 3;

const { listTasks: listTasksUrl } = urlConstants.tasks;
const { label: labelStyle } = EquipUITheme.typography;

enum PatientTaskFilterIndex {
  MaudTasks = 0,
  FamilyTasks = 1,
}

const TAB_LIST_ID = 'PATIENT_TASK_FILTER_TAB';

const mapTasksToGridItems = (
  data: CompServTask[],
  categories: Category[],
  onTaskSelect: (taskId: string) => void,
): ReactElement[] => {
  return (data ?? [])
    .map((task) => mapCompServTaskToTask(task, categories))
    .map((task: Task) => (
      <ChakraGridItem
        key={`patient-tasks-${task.taskExternalId}`}
        marginTop="3px"
      >
        <TaskListItem
          isFamilyTask={task.isFamilyTask}
          isPatientProfile
          onTaskSelect={() => {
            onTaskSelect(task.taskExternalId);
          }}
          task={task}
        />
      </ChakraGridItem>
    ));
};

interface PatientTasksOverviewProps {
  detailsTabId?: string;
  openTasks?: CompServTask[];
  patientId: string;
}

export const PatientTasksOverview = ({
  detailsTabId = '',
  openTasks,
  patientId,
}: PatientTasksOverviewProps) => {
  const history = useHistory();
  const { categories } = useTaskContext();

  const onTaskSelect = useCallback(
    (value: string) => {
      history.push({
        pathname: `${Path.MAUD_PATIENTS}/${patientId}/${
          PatientTabsCollection[PatientTabs.TASKS].subPath
        }/${value}`,
        state: {
          fromPatientRouteTab: false,
          fromTaskRoute: false,
        },
      });
    },
    [patientId],
  );

  if (openTasks == null) return <Loading />;
  if (!openTasks?.length) return <NoData />;
  // Show the Task Detail page if a task has been selected
  if (detailsTabId) return <TaskDetail taskId={detailsTabId} />;

  return (
    <ChakraSimpleGrid columns={1} spacing="8px">
      {mapTasksToGridItems(
        openTasks.slice(0, TASKS_PREVIEW_PAGE_SIZE),
        categories,
        onTaskSelect,
      )}
    </ChakraSimpleGrid>
  );
};

interface PatientTasksProps {
  detailsTabId?: string;
  familyTaskCount?: number;
  maudTaskCount?: number;
  patientId: string;
  selectedTaskTab?: number;
  setSelectedTaskTab?: (selectedTaskTab: number) => void;
}

const PatientTasks = ({
  detailsTabId = '',
  familyTaskCount,
  maudTaskCount,
  patientId,
  selectedTaskTab,
  setSelectedTaskTab,
}: PatientTasksProps) => {
  const [tasks, setTasks] = useState<ReactElement[]>([]);
  const [tasksCount, setTasksCount] = useState<number>(null);
  const [tasksPage, setTasksPage] = useState<number>(0);
  const [filterTabIndex, setFilterTabIndex] = useState<number>(selectedTaskTab);
  const [familyTaskFilterEnabled, setFamilyTaskFilterEnabled] =
    useState<boolean>(selectedTaskTab === 1);

  const { trackEvent } = useAnalytics();

  const [shouldFetchTasks, setShouldFetchTasks] = useState<boolean>(false);
  const history = useHistory();

  const onTaskSelect = useCallback(
    (value: string) => {
      history.push({
        pathname: `${Path.MAUD_PATIENTS}/${patientId}/${
          PatientTabsCollection[PatientTabs.TASKS].subPath
        }/${value}`,
        state: {
          fromPatientRouteTab: true,
          fromTaskRoute: false,
        },
      });
    },
    [patientId],
  );

  const { categories } = useTaskContext();

  const { sendRequest: getPatientTasksData } = useApi<FetchTasksResponse>();

  const handleTabIndexChange = useCallback(
    (index: number) => {
      if (filterTabIndex === index) {
        return;
      }

      const event =
        index === PatientTaskFilterIndex.FamilyTasks
          ? TaskEvents.patientAndSupportsTaskFilterSelectedFromTasksTabPanel
          : TaskEvents.equipStaffTaskFilterSelectedFromTasksTabPanel;

      trackEvent(event, {
        Source: AnalyticsSourceValues.TASKS_TAB,
        patientId,
        FilterName:
          index === PatientTaskFilterIndex.FamilyTasks
            ? 'Patient and Supports'
            : 'Equip staff',
      });

      setFilterTabIndex(index);
      setSelectedTaskTab(index);
      setTasksCount(null);
      setTasks([]);
      setFamilyTaskFilterEnabled(
        index === PatientTaskFilterIndex.FamilyTasks.valueOf(),
      );
      setShouldFetchTasks(true);
    },
    [filterTabIndex],
  );

  const handleFetchPatientTasksResponse = useCallback(
    ({ data, pageNumber, totalCount }: FetchTasksResponse): void => {
      const newTasksGridItems = mapTasksToGridItems(
        data,
        categories,
        onTaskSelect,
      );
      if (pageNumber === 1) {
        setTasks(newTasksGridItems);
      } else {
        // request coming from infiniteScroll, concat instead of replace
        setTasks((prev: ReactElement[]) => prev.concat(newTasksGridItems));
      }

      setTasksPage((prevPage: number) => pageNumber ?? prevPage);
      setTasksCount((prevCount: number) => totalCount ?? prevCount);
    },
    [categories, onTaskSelect],
  );

  const fetchPatientTasks = useCallback(
    (pageNumber?: number): void => {
      if (
        !isPatientTaskDetailRoute(window.location.pathname) &&
        (pageNumber === 1 || tasksCount === null || tasks.length < tasksCount)
      ) {
        const options = {
          'filter-by': {
            'is-family-task': familyTaskFilterEnabled,
            'patient-external-id': patientId,
          },
          'order-by': familyTaskFilterEnabled
            ? OrganizeBy.CREATED_DATE
            : OrganizeBy.DUE_DATE,
          'page-number': pageNumber ?? tasksPage + 1,
          'page-size': ALL_TASKS_PAGE_SIZE,
        };

        getPatientTasksData({
          callback: handleFetchPatientTasksResponse,
          command: ApiCommand.GET,
          options,
          url: listTasksUrl,
        });
      }
    },
    [
      familyTaskFilterEnabled,
      getPatientTasksData,
      handleFetchPatientTasksResponse,
      patientId,
      tasks?.length,
      tasksCount,
      tasksPage,
    ],
  );

  useEffect(() => {
    if (!isPatientTaskDetailRoute(window.location.pathname)) {
      fetchPatientTasks(1);
    }
  }, []);

  const { addListener, removeListener } = useGlobalContext();

  useEffect(() => {
    addListener(Listeners.PATIENTTASKS_TASKDETAILS_CREATE, () =>
      fetchPatientTasks(1),
    );
    addListener(Listeners.PATIENTTASKS_TASKDETAILS_UPDATE, () =>
      fetchPatientTasks(1),
    );

    return () => {
      removeListener(Listeners.PATIENTTASKS_TASKDETAILS_CREATE);
      removeListener(Listeners.PATIENTTASKS_TASKDETAILS_UPDATE);
    };
  }, []);

  const TaskTabAddons = ({ count }: { count: number }) => {
    return (
      <Badge
        badgeColor="tertiary.marigold.base"
        fontSize="10px"
        id="patient-menu__tasks"
        margin="0 0 0 4px"
      >
        {count}
      </Badge>
    );
  };

  const TasksFilter = useMemo(() => {
    return (
      <ChakraFlex alignItems="center" marginTop="20px">
        <ChakraText
          color="neutral.secondary"
          marginRight="20px"
          {...labelStyle}
        >
          Show tasks for
        </ChakraText>
        <ChakraTabs
          defaultIndex={0}
          index={filterTabIndex}
          isLazy
          onChange={handleTabIndexChange}
          variant="soft-rounded"
        >
          <ChakraTabList
            background="neutral.background.secondary"
            borderRadius="29px"
            height="42px"
            id="patient-tab-list"
            padding="6px"
          >
            {Object.values([
              { count: maudTaskCount, label: 'Equip staff' },
              {
                count: familyTaskCount,
                label: 'Patient and Supports',
              },
            ]).map(({ count, label }, index) => (
              <ChakraTab
                _selected={{
                  backgroundColor: 'primary.100',
                  boxShadow: 'none',
                  color: 'white',
                }}
                color="neutral.secondary"
                id={`${TAB_LIST_ID}__${index}`}
                isDisabled={false}
                key={`tasks-filter-tab-${label}`}
                marginRight="8px"
                padding="6px 12px"
              >
                <ChakraText
                  {...labelStyle}
                  id={`${TAB_LIST_ID}__${index}-label`}
                  lineHeight="17px"
                >
                  {label}
                </ChakraText>
                {count > 0 && <TaskTabAddons count={count} />}
              </ChakraTab>
            ))}
          </ChakraTabList>
        </ChakraTabs>
      </ChakraFlex>
    );
  }, [filterTabIndex, maudTaskCount, familyTaskCount]);

  const emptyTaskList = useMemo(
    () => (
      <>
        {TasksFilter}
        <ChakraBox paddingTop="36px">
          <EmptyTaskList
            description={getEmptyTaskContent('description', false)}
            primaryText={getEmptyTaskContent('primaryText', false)}
            secondaryText={getEmptyTaskContent('secondaryText', false)}
          />
        </ChakraBox>
      </>
    ),
    [TasksFilter],
  );

  useEffect(() => {
    if (shouldFetchTasks) {
      fetchPatientTasks(1);
      setShouldFetchTasks(false);
    }
  }, [shouldFetchTasks]);

  if (
    tasksCount === null &&
    !isPatientTaskDetailRoute(window.location.pathname)
  ) {
    return <Loading />;
  }

  if (detailsTabId) return <TaskDetail taskId={detailsTabId} />;

  // Show a custom message if there are no tasks
  if (!tasks?.length) return emptyTaskList;

  return (
    <ChakraSimpleGrid columns={1} spacing="8px">
      <>
        {TasksFilter}

        <TaskList
          data={[
            {
              count: tasksCount,
              items: tasks,
              key: patientId,
              title: '',
            },
          ]}
          fetchNextBatch={fetchPatientTasks}
          height={TASKS_CONTAINER_HEIGHT}
          hideSectionHeaders
          totalTasksCount={tasksCount}
        />
      </>
    </ChakraSimpleGrid>
  );
};

export default PatientTasks;
